import DOMPurify from 'dompurify'
import { HTML_TEMPLATE, SANITIZE_OPTIONS } from 'pages/dashboard/pages/dispatcher/pages/dispatcher-emails/components/EmailContent/components/EmailCard/const'

// Pure CSS can't beat author inline `style="color: ... !important"`, so for
// dark mode we drop those declarations (and `<font color>` attrs) before
// rendering. The outer `* { color: inherit !important }` override then wins,
// inheriting the page's light text color and making content readable on the
// dark surface.
function stripAuthorTextColors(html: string): string {
    const stripFromStyle = (styleBody: string) =>
        styleBody
            .replace(/(?:^|;)\s*color\s*:[^;]*/gi, '')
            .replace(/^\s*;+/, '')
            .trim()
    return html
        .replace(/style\s*=\s*"([^"]*)"/gi, (_, body) => `style="${stripFromStyle(body)}"`)
        .replace(/style\s*=\s*'([^']*)'/gi, (_, body) => `style='${stripFromStyle(body)}'`)
        .replace(/(<font\b[^>]*?)\s+color\s*=\s*(?:"[^"]*"|'[^']*'|\S+)/gi, '$1')
}

export function buildThemedEmailHtml(rawBody: string, mode: 'light' | 'dark' | 'system' = 'light') {
    let cleanContent = DOMPurify.sanitize(rawBody || '', SANITIZE_OPTIONS)

    // Normalize mode for color-scheme
    const effectiveMode: 'light' | 'dark' = mode === 'dark' ? 'dark' : 'light'

    if (effectiveMode === 'dark') {
        cleanContent = stripAuthorTextColors(cleanContent)
    }

    // Let your existing template build the base HTML structure
    let baseHtml = HTML_TEMPLATE(cleanContent, effectiveMode)

    // Try to read Joy CSS vars from the main app document (if we're in browser)
    let textPrimary = ''
    let textSecondary = ''
    let primaryColor = ''

    if (typeof document !== 'undefined') {
        try {
            const rootStyles = getComputedStyle(document.documentElement)

            textPrimary = rootStyles.getPropertyValue('--joy-palette-text-primary').trim() || textPrimary

            textSecondary = rootStyles.getPropertyValue('--joy-palette-text-secondary').trim() || textSecondary

            primaryColor = rootStyles.getPropertyValue('--joy-palette-primary-500').trim() || primaryColor
        } catch {
            // if it fails, we just use the fallbacks above
        }
    }

    const overrideCss = `
    :root {
      color-scheme: ${effectiveMode};
    }

    html, body {
      margin: 0;
      padding: 0;
      background: transparent !important;
      color: ${textPrimary} !important;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-size: 14px;
    }

    /* Kill Gmail inline text/background colors */
    * {
      color: inherit !important;
      background-color: transparent !important;
    }

    a, a * {
      color: ${primaryColor} !important;
      text-decoration: underline;
    }

    blockquote {
      border-left: 2px solid ${textSecondary} !important;
      margin-left: 0;
      padding-left: 0.75rem;
    }

    img {
      max-width: 100%;
      height: auto;
    }
  `

    const styleTag = `<style>${overrideCss}</style>`

    // Inject our style before </head> if possible, otherwise just prepend it
    if (baseHtml.includes('</head>')) {
        baseHtml = baseHtml.replace('</head>', `${styleTag}</head>`)
    } else {
        baseHtml = styleTag + baseHtml
    }

    return baseHtml
}
